import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Truck, ExternalLink, Shield } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { HN_COLLECTIONS, SHOPIFY_URL } from "../data/shopifyCollection";

const NEW_DROP = { collection: "The Called Out Ones", title: "Believe · Set Apart · Chosen", copy: "Collection 01 is here. Heavyweight cotton. Distressed cross. Vintage wash." };

const ProductCard = ({ product, onBuy }) => (
  <motion.article
    data-testid={`hn-product-${product.id}`}
    whileHover={{ y: -6 }}
    transition={{ type: "spring", stiffness: 200, damping: 20 }}
    className="group flex flex-col bg-[#0B0B0B] border border-[#C0953A]/20 hover:border-[#C0953A] transition-colors overflow-hidden"
  >
    <div className="aspect-[4/3] overflow-hidden bg-[#0B0B0B]">
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
      />
    </div>
    <div className="p-6 flex-1 flex flex-col">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#C0953A]">
        {product.collection} · {product.drop}
      </p>
      <h3 className="mt-2 font-serif-d text-2xl text-[#FAF4E9]">{product.name}</h3>
      <p className="mt-2 text-sm text-[#FAF4E9]/60 italic">&ldquo;{product.tagline}&rdquo;</p>
      <p className="mt-3 text-xs text-[#FAF4E9]/40">{product.scripture}</p>
      <div className="mt-auto pt-5 flex items-center justify-between gap-3">
        <span className="font-serif-d text-2xl text-[#DCBB6C]">${product.price.toFixed(0)}</span>
        <button
          data-testid={`hn-buy-${product.id}`}
          onClick={() => onBuy(product)}
          className="bg-[#C0953A] hover:bg-[#DCBB6C] text-black uppercase tracking-[0.16em] text-[11px] font-semibold px-5 py-3 transition-colors"
        >
          Buy Now
        </button>
      </div>
    </div>
  </motion.article>
);

const CheckoutModal = ({ product, onClose }) => {
  const [size, setSize] = useState(product.sizes[0]);
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", address_line1: "", address_line2: "",
    city: "", state: "", postal_code: "", country: "US",
  });
  const [step, setStep] = useState("select"); // select -> shipping -> processing
  const [subtotal, setSubtotal] = useState(product.price);
  const [shipping, setShipping] = useState(product.price >= 55 ? 0 : 8);

  useEffect(() => {
    const s = +(product.price * qty).toFixed(2);
    setSubtotal(s);
    setShipping(s >= 55 ? 0 : 8);
  }, [qty, product.price]);

  const total = +(subtotal + shipping).toFixed(2);

  const handleCheckout = async () => {
    if (!form.name || !form.email || !form.address_line1 || !form.city || !form.state || !form.postal_code) {
      toast.error("Please fill in all shipping fields.");
      return;
    }
    setStep("processing");
    try {
      const { data } = await api.post("/store/checkout", {
        product_id: product.id,
        size,
        quantity: qty,
        shipping: form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setStep("shipping");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="hn-checkout-modal">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-[#0B0B0B] border border-[#C0953A]/30 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between p-6 border-b border-[#C0953A]/20">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C0953A]">{product.collection}</p>
            <h2 className="font-serif-d text-3xl text-[#FAF4E9] mt-1">{product.name}</h2>
          </div>
          <button data-testid="hn-modal-close" onClick={onClose} className="text-[#FAF4E9]/60 hover:text-[#FAF4E9]"><X size={22} /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-0">
          <div className="bg-[#0B0B0B]">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>

          <div className="p-6 md:p-8 text-[#FAF4E9]">
            {step !== "shipping" && step !== "processing" && (
              <>
                <p className="text-sm text-[#FAF4E9]/70 leading-relaxed">{product.description}</p>
                <p className="mt-4 text-[11px] uppercase tracking-widest text-[#C0953A]">Scripture · {product.scripture}</p>

                <div className="mt-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#FAF4E9]/50 mb-3">Size</p>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        data-testid={`hn-size-${s}`}
                        onClick={() => setSize(s)}
                        className={`min-w-[52px] px-4 py-3 text-xs uppercase tracking-widest border transition-colors ${
                          size === s ? "bg-[#C0953A] text-black border-[#C0953A]" : "border-[#FAF4E9]/25 text-[#FAF4E9] hover:border-[#C0953A]"
                        }`}
                      >{s}</button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#FAF4E9]/50 mb-3">Quantity</p>
                  <div className="flex items-center gap-4">
                    <button data-testid="hn-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 border border-[#FAF4E9]/25 hover:border-[#C0953A]">−</button>
                    <span className="font-serif-d text-2xl w-8 text-center" data-testid="hn-qty-val">{qty}</span>
                    <button data-testid="hn-qty-plus" onClick={() => setQty(Math.min(10, qty + 1))} className="w-10 h-10 border border-[#FAF4E9]/25 hover:border-[#C0953A]">+</button>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-[#C0953A]/15 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#FAF4E9]/60">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-[#FAF4E9]/60">Shipping {subtotal >= 55 && <span className="text-[#8FA88B] text-xs">· FREE over $55</span>}</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
                  <div className="flex justify-between font-serif-d text-2xl text-[#DCBB6C] pt-2"><span>Total</span><span data-testid="hn-total-val">${total.toFixed(2)}</span></div>
                </div>

                <button
                  data-testid="hn-continue-shipping"
                  onClick={() => setStep("shipping")}
                  className="w-full mt-6 bg-[#C0953A] hover:bg-[#DCBB6C] text-black uppercase tracking-[0.18em] text-xs font-semibold py-4"
                >Continue to Shipping →</button>
                <p className="mt-3 text-[10px] text-[#FAF4E9]/40 text-center flex items-center justify-center gap-2"><Shield size={12} /> Secure checkout by Stripe</p>
              </>
            )}

            {step === "shipping" && (
              <>
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#C0953A]">Shipping to</p>
                <h3 className="font-serif-d text-2xl mt-1">Where should we send it?</h3>
                <div className="mt-5 space-y-3">
                  <input data-testid="hn-ship-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                  <input data-testid="hn-ship-email" type="email" placeholder="Email for order updates" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                  <input data-testid="hn-ship-addr1" placeholder="Street address" value={form.address_line1} onChange={(e) => setForm({...form, address_line1: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                  <input data-testid="hn-ship-addr2" placeholder="Apt / Suite (optional)" value={form.address_line2} onChange={(e) => setForm({...form, address_line2: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="hn-ship-city" placeholder="City" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                    <input data-testid="hn-ship-state" placeholder="State" value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="hn-ship-zip" placeholder="ZIP / Postal code" value={form.postal_code} onChange={(e) => setForm({...form, postal_code: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                    <input data-testid="hn-ship-country" placeholder="Country" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} className="w-full bg-transparent border-b border-[#FAF4E9]/25 focus:border-[#C0953A] py-2 text-sm outline-none" />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-[#C0953A]/15 space-y-1 text-sm">
                  <div className="flex justify-between text-[#FAF4E9]/70"><span>{product.name} · {size} × {qty}</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-[#FAF4E9]/70"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
                  <div className="flex justify-between font-serif-d text-xl text-[#DCBB6C] pt-1"><span>Total</span><span>${total.toFixed(2)}</span></div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button data-testid="hn-back-select" onClick={() => setStep("select")} className="flex-1 border border-[#FAF4E9]/25 hover:border-[#C0953A] uppercase tracking-[0.16em] text-[11px] py-4">← Back</button>
                  <button data-testid="hn-pay-btn" onClick={handleCheckout} className="flex-[2] bg-[#C0953A] hover:bg-[#DCBB6C] text-black uppercase tracking-[0.18em] text-xs font-semibold py-4">Pay ${total.toFixed(2)} →</button>
                </div>
              </>
            )}

            {step === "processing" && (
              <div className="flex flex-col items-center justify-center py-16">
                <Crown size={40} className="text-[#C0953A] animate-pulse" />
                <p className="mt-6 uppercase tracking-[0.22em] text-xs text-[#C0953A]">Opening Stripe…</p>
                <p className="mt-2 text-sm text-[#FAF4E9]/60">Preparing your checkout</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Store() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/store/products").then(({ data }) => setProducts(data.products)).catch(() => {});
    document.title = "Haus of Nata · Live On Purpose";
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-[#FAF4E9]">
      <Nav />

      {/* NEW DROPS BANNER */}
      <div data-testid="hn-drop-banner" className="pt-24 pb-2 bg-gradient-to-b from-[#0B0B0B] to-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 bg-[#C0953A]/10 border border-[#C0953A]/30 py-3 px-6 text-center">
            <Crown size={16} className="text-[#DCBB6C]" />
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#DCBB6C]">
              NEW DROP · {NEW_DROP.collection} · <span className="text-[#FAF4E9]/70 normal-case tracking-normal">{NEW_DROP.copy}</span>
            </p>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
          <p className="eyebrow text-[#DCBB6C]">Haus of Nata · Volume 01</p>
          <h1 className="font-serif-d text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] tracking-tight">
            Born to <span className="italic-emph text-[#C0953A]">Reign.</span> <br />Show it.
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-[#FAF4E9]/60 text-base sm:text-lg leading-relaxed">
            This isn&apos;t just clothing. It&apos;s a declaration. Luxury streetwear built for the called, the crowned, and the ones who choose purpose over the crowd.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center text-[11px] uppercase tracking-[0.22em] text-[#FAF4E9]/50">
            <span className="border border-[#C0953A]/30 px-4 py-2">Faith</span>
            <span className="border border-[#C0953A]/30 px-4 py-2">Identity</span>
            <span className="border border-[#C0953A]/30 px-4 py-2">Freedom</span>
          </div>
          <div className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#8FA88B]">
            <Truck size={14} /> Free U.S. Shipping on orders over $55
          </div>
        </div>
      </section>

      {/* HERO PRODUCTS */}
      <section data-testid="hn-hero-products" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="eyebrow text-[#C0953A]">Curated · Shop the drop</p>
            <h2 className="font-serif-d text-3xl sm:text-4xl mt-2">The <span className="italic-emph text-[#DCBB6C]">Top Picks</span></h2>
          </div>
          <p className="text-sm text-[#FAF4E9]/50 max-w-xs">Checkout instantly here — full collection available on our Shopify boutique.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} onBuy={setSelected} />
          ))}
        </div>
      </section>

      {/* FULL COLLECTION → SHOPIFY */}
      <section data-testid="hn-full-collection" className="border-t border-[#C0953A]/15 py-24 bg-gradient-to-b from-[#0B0B0B] to-[#111]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="eyebrow text-[#C0953A]">The Full Line — Volume 01</p>
            <h2 className="font-serif-d text-4xl sm:text-5xl mt-3">Every drop. Every declaration.</h2>
            <p className="mt-5 text-[#FAF4E9]/55 max-w-2xl mx-auto">Browse the complete Haus of Nata collection — from the Called Out Ones to the Luxe Line — on our boutique.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {HN_COLLECTIONS.map((c) => (
              <a
                key={c.id}
                data-testid={`hn-shopify-${c.id}`}
                href={`${SHOPIFY_URL}/collections/all`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-[#0B0B0B] border border-[#C0953A]/20 hover:border-[#C0953A] transition-all p-8 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 opacity-10" style={{ background: `radial-gradient(circle at top right, ${c.accent}, transparent 70%)` }} />
                <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: c.accent }}>{c.tagline}</p>
                <h3 className="font-serif-d text-3xl mt-2 group-hover:text-[#DCBB6C] transition-colors">{c.title}</h3>
                <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-sm text-[#FAF4E9]/60">
                  {c.items.slice(0, 6).map((i) => (
                    <span key={i.name}>{i.name} <span className="text-[#FAF4E9]/35">${i.price}</span></span>
                  ))}
                  {c.items.length > 6 && <span className="text-[#C0953A]/70">+{c.items.length - 6} more</span>}
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#C0953A] group-hover:text-[#DCBB6C]">Shop this collection</span>
                  <ExternalLink size={16} className="text-[#C0953A] group-hover:text-[#DCBB6C]" />
                </div>
              </a>
            ))}
          </div>

          <div className="mt-14 text-center">
            <a
              data-testid="hn-shop-all-btn"
              href={SHOPIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#C0953A] hover:bg-[#DCBB6C] text-black px-10 py-5 uppercase tracking-[0.22em] text-xs font-bold transition-colors"
            >
              Enter The Boutique <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* BRAND MANIFESTO */}
      <section className="py-24 border-t border-[#C0953A]/15">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Crown size={32} className="text-[#C0953A] mx-auto" />
          <p className="mt-6 font-serif-d text-2xl sm:text-3xl leading-relaxed italic text-[#FAF4E9]/85">
            &ldquo;You don&apos;t fit in. You stand out. You choose.&rdquo;
          </p>
          <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-[#C0953A]">Faith · Identity · Freedom</p>
        </div>
      </section>

      <AnimatePresence>{selected && <CheckoutModal product={selected} onClose={() => setSelected(null)} />}</AnimatePresence>

      <Footer />
    </div>
  );
}
